import {
	startOfDay,
	startOfISOWeek,
	startOfMonth,
	startOfQuarter,
	startOfWeek,
	endOfDay,
	endOfISOWeek,
	endOfMonth,
	endOfQuarter,
	endOfWeek,
	addMilliseconds,
	addMonths,
	addWeeks,
	addDays,
} from 'date-fns';
import memoizeOne from 'memoize-one';
import {
	TimelineModeOptions,
	type TimelineDuration,
	type ColumnDuration,
	type TimelineMode,
} from '../model/timeline';

const { DAYS, WEEKS, QUARTERS, MONTHS } = TimelineModeOptions;
const getColumnDurationsForMode = (
	timelineDuration: TimelineDuration,
	momentIdentifier: string,
): ColumnDuration[] => {
	const columnDurations: Array<ColumnDuration> = [];
	const { startDate, endDate } = timelineDuration;
	// Sanitize. We still want to *round* to an ISO week, but you can't *add* an ISO week (it fails silently).
	const momentAddIdentifier = momentIdentifier === 'isoWeek' ? 'week' : momentIdentifier;
	const startOfswitch = (identifierName = 'day') => {
		const startOfFuncs = {
			day: startOfDay,
			week: startOfWeek,
			isoWeek: startOfISOWeek,
			month: startOfMonth,
			quarter: startOfQuarter,
		};
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ day: (date: number | Date) => Date; week: (date: number | Date, options?: { locale?: Locale | undefined; weekStartsOn?: 0 | 1 | 2 | 4 | 3 | 5 | 6 | undefined; } | undefined) => Date; isoWeek: (date: number | Date) => Date; month: (date: number | Date) => Date; quarter: (date: number | Date) => Date; }'.
		return startOfFuncs[identifierName];
	};
	const endOfswitch = (identifierName = 'day') => {
		const endOfFuncs = {
			day: endOfDay,
			week: endOfWeek,
			isoWeek: endOfISOWeek,
			month: endOfMonth,
			quarter: endOfQuarter,
		};
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ day: (date: number | Date) => Date; week: (date: number | Date, options?: { locale?: Locale | undefined; weekStartsOn?: 0 | 1 | 2 | 4 | 3 | 5 | 6 | undefined; } | undefined) => Date; isoWeek: (date: number | Date) => Date; month: (date: number | Date) => Date; quarter: (date: number | Date) => Date; }'.
		return endOfFuncs[identifierName];
	};
	const addTimeSwitch = (identifierName = 'day') => {
		const addTimeFuncs = {
			day: addDays,
			week: addWeeks,
			month: addMonths,
		};
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ day: (date: number | Date, amount: number) => Date; week: (date: number | Date, amount: number) => Date; month: (date: number | Date, amount: number) => Date; }'.
		return addTimeFuncs[identifierName];
	};
	const startOf = startOfswitch(momentIdentifier);
	const endOf = endOfswitch(momentIdentifier);
	let startDateFns = startOf(startDate);
	const addTime = addTimeSwitch(momentAddIdentifier);

	while (startDateFns.valueOf() < endDate) {
		const endMoment = addMilliseconds(endOf(startDateFns), 1);
		columnDurations.push({
			startDate: new Date(startDateFns.valueOf()),
			endDate: new Date(endMoment.valueOf()),
		});
		startDateFns = addTime(startDateFns, 1);
	}
	return columnDurations;
};

/* Get the duration of each column within the timeline.
 * A column is exactly one unit of time based on the specific timeline mode,
 * (e.g. one week, one month, one quarter).
 */
export const getColumnDurationsByDateRange = memoizeOne(
	(timelineMode: TimelineMode, timelineDuration: TimelineDuration): ColumnDuration[] => {
		if (timelineMode === QUARTERS) {
			return getColumnDurationsForMode(timelineDuration, 'quarter');
		}
		if (timelineMode === MONTHS) {
			return getColumnDurationsForMode(timelineDuration, 'month');
		}
		/* We always want a week column to start on Monday. The 'week' parameter
		 * is locale aware which could start on Sunday. An ISO week on the other
		 * hand always starts on the Monday.
		 */
		if (timelineMode === WEEKS) {
			return getColumnDurationsForMode(timelineDuration, 'isoWeek');
		}

		if (timelineMode === DAYS) {
			return getColumnDurationsForMode(timelineDuration, 'day');
		}
		throw Error('mode not found');
	},
);
